import { Alert, Button, Icon, Input, Modal, Spin, Tree } from "antd";
import { ModalProps } from "antd/lib/modal";
import React, { useState } from "react";

import style from "./index.module.scss";
import { curSessionIdAtom, userAtom } from "../../store";
import { useAtomValue, useSetAtom } from "jotai";
import { v4 as uuid } from "uuid";

import {
    CurrentUserType,
    CustomEmpty,
    errorMessage,
    getContactList,
    getSessionInfo,
    isTrue,
    openSession,
    OppositeUserType,
    ResultMessage,
    SessionType,
    useRequest,
    useRequestFn
} from "../../polyfills";

import useUpdateSession from "../../Utils/useUpdateSession";

const { Search } = Input;
const { TreeNode } = Tree;

interface MembersListProps extends ModalProps {
    onCancel: () => void;
    onSuccess: (val: string[]) => void;
}

export default function MembersList({
    onCancel,
    onSuccess,
    ...resProps
}: MembersListProps) {
    const { userNo, userName, isStu } = useAtomValue(userAtom);
    const setCurSessionId = useSetAtom(curSessionIdAtom);
    const sessionManager = useUpdateSession();

    const [search, setSearch] = useState("");
    const [select, setSelect] = useState<string[]>([]);

    const [contactTreeData, { loading: contactLoading }] = useRequest(
        () => getContactList(search),
        {},
        [search]
    );

    const [openChat, { loading }] = useRequestFn(
        async (params: Parameters<typeof openSession>[0]) => {
            const data = await openSession(params);
            return await getSessionInfo(data.guid);
        }
    );

    const onSearch = (value: string) => {
        setSearch(value);
    };

    const onSelect = (selectedKeys: string[]) => {
        setSelect(selectedKeys[0]?.split("-"));
    };

    //打开会话
    async function onAdd() {
        const params = {
            guid: uuid(),
            chatConferees: [
                {
                    no: userNo,
                    name: userName,
                    type: CurrentUserType
                },
                {
                    no: select?.[0],
                    name: select?.[1],
                    type: OppositeUserType
                }
            ],
            sessionType: SessionType.One
        };
        try {
            const session = await openChat(params);
            sessionManager.addSession(session);

            if (session) {
                setCurSessionId(session.guid);
                onSuccess(select);
            }
        } catch (e) {
            ResultMessage.error(errorMessage(e));
        }
    }

    const footer = (
        <div>
            <Button onClick={() => onCancel()}>取消</Button>
            <Button
                onClick={onAdd}
                type="primary"
                loading={loading}
                disabled={!select?.[0]}>
                确认
            </Button>
        </div>
    );

    function renderTreeNodes(data?: any[]) {
        return data?.map(item => {
            if (item.children?.length > 0) {
                return (
                    <TreeNode
                        title={
                            <div>
                                {item.children?.length > 1 && (
                                    <Icon type="team" className={style.icon} />
                                )}
                                {item.title}
                            </div>
                        }
                        key={item.key}
                        disabled={item.disabled}
                        className={style.node}>
                        {renderTreeNodes(item.children)}
                    </TreeNode>
                );
            } else {
                return (
                    <TreeNode
                        key={item.key}
                        title={
                            <>
                                {item.title}
                                {isTrue(item.isMain) && data.length > 1 && (
                                    <span className={style.majorflag}>
                                        主讲
                                    </span>
                                )}
                            </>
                        }
                        disabled={item.disabled}
                        className={style.node}>
                        {item.title}
                    </TreeNode>
                );
            }
        });
    }

    const alertMsg = (
        <div className={style.alertMsg}>
            说明：有&nbsp;&nbsp;
            <Icon type="team" style={{ color: "#1890ff" }} />
            &nbsp;&nbsp;
            代表本门课程为教师团授课，需要选择对应的授课教师发起私信。考试相关请优先咨询主讲教师。
        </div>
    );
    return (
        <Modal
            width={610}
            title="选择联系人"
            className={style.root}
            onCancel={onCancel}
            footer={footer}
            {...resProps}>
            <Search
                style={{ marginBottom: 8 }}
                maxLength={50}
                allowClear
                placeholder={`请输入联系人姓名或${
                    isStu ? "教工号" : "学号"
                }关键字`}
                onSearch={onSearch}
            />

            {isStu && (
                <Alert message={alertMsg} type="warning" showIcon closable />
            )}

            <Spin spinning={contactLoading}>
                {contactTreeData && contactTreeData?.length > 0 && (
                    <Tree
                        showIcon
                        defaultExpandAll
                        onSelect={onSelect}
                        className={style.treeCotainer}>
                        {renderTreeNodes(contactTreeData || [])}
                    </Tree>
                )}
                {contactTreeData?.length === 0 && (
                    <CustomEmpty
                        description="暂无联系人"
                        style={{ padding: "100px 0" }}
                    />
                )}
            </Spin>
        </Modal>
    );
}
